import { useCallback, useContext, useEffect, useState } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  applyActionCode,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  setPersistence,
  browserSessionPersistence,
  onAuthStateChanged,
} from "firebase/auth";
import { FirebaseError } from "@firebase/util";
import { AxiosError } from "axios";

import { auth } from "config/firebase";
import { ChildrenElements } from "utils/interfaces/commonInterfacesAndTypes";
import AuthContext from "./auth-context";
import {
  SignInRequest,
  SignUpRequest,
} from "services/HTTPService/responseRequestTypes";
import HTTPService from "services/HTTPService/HTTPService";
import { useRouter } from "next/router";
import RegisterUserForm from "../../user/RegisterUserForm";
import AuthApi from "../../user/auth/AuthApi";
import User from "../../user/User";
import UserApi from "../../user/UserApi";
import { useQuery } from "@tanstack/react-query";
import UserProfileApi from "../../user/UserProfileApi";
import PartnerProfile from "../../user/partner/PartnerProfile";
import ClientProfile from "../../user/client/ClientProfile";
import {
  useAddPartnerStore,
  useAddPartnerStoreSetup,
} from "../partner/addPartnerStore";
import { useEditPartnerStoreSetup } from "../partner/editPartnerStore";

const AuthContextProvider = ({ children }: ChildrenElements) => {
  const store = useAddPartnerStore();
  const [user, setUser] = useState<User | null>(null);
  const [partner, setPartner] = useState<PartnerProfile | null>(null);
  const [client, setClient] = useState<ClientProfile | null>(null);
  const [recentlyLoggedOut, setRecentlyLoggedOut] = useState(false);
  const router = useRouter();
  const editPartnerStoreSetup = useEditPartnerStoreSetup();
  const addPartnerStoreSetup = useAddPartnerStoreSetup();

  const assignUser = useCallback((userToAssign: User) => {
    setUser(userToAssign);
    setRecentlyLoggedOut(false);
    localStorage.setItem("userId", userToAssign._id);
  }, []);

  const signUp = async ({
    email,
    phonePrefix,
    phoneNumber,
    password,
    accountType,
    isAgreementAccepted,
  }: RegisterUserForm) => {
    try {
      const response = await new UserApi().create({
        email,
        phonePrefix,
        phoneNumber,
        password,
        accountType,
        isAgreementAccepted,
      });
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data.error);
      } else {
        const typedError = error as Error;
        throw new Error(typedError.message);
      }
    }
  };

  const signIn = useCallback(
    async ({ email, password }: SignInRequest, enabledAutologin: boolean) => {
      try {
        const response = await new AuthApi().login({
          username: email,
          password,
        });

        if (response?.user?._id) {
          assignUser(response.user);
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          throw new Error(error.code);
        } else {
          const typedError = error as Error;
          throw new Error(typedError.message);
        }
      }
    },
    []
  );

  const logout = async () => {
    try {
      // await signOut(auth);
      setUser(null);
      setRecentlyLoggedOut(true);
      localStorage.setItem("userId", "");
    } catch (error) {
      const typedError = error as Error;
      throw new Error(typedError.message);
    }
  };

  const verifyEmail = async (oobCode: string) => {
    return;

    // try {
    //   await applyActionCode(auth, oobCode);
    // } catch (error) {
    //   if (error instanceof FirebaseError) {
    //     throw new Error(error.code);
    //   } else {
    //     const typedError = error as Error;
    //     throw new Error(typedError.message);
    //   }
    // }
  };

  const changePassword = async (
    actualPassword: string,
    newPassword: string
  ) => {
    return;

    // try {
    //   const currentUser = auth.currentUser;
    //
    //   if (currentUser) {
    //     const credential = EmailAuthProvider.credential(
    //       currentUser.email!,
    //       actualPassword
    //     );
    //
    //     await reauthenticateWithCredential(currentUser, credential);
    //
    //     if (actualPassword === newPassword) {
    //       throw new Error(NEW_PASSWORD_EQUAL_ACTUAL);
    //     }
    //
    //     await updatePassword(currentUser, newPassword);
    //   } else {
    //     throw new Error("missing:error_while_changing_password");
    //   }
    // } catch (error) {
    //   if (error instanceof FirebaseError) {
    //     throw new Error(error.code);
    //   } else {
    //     const typedError = error as Error;
    //     throw new Error(typedError.message);
    //   }
    // }
  };

  const changeEmail = async (
    currentEmail: string,
    newEmail: string,
    password: string
  ) => {
    return;

    // try {
    //   const currentUser = auth.currentUser;
    //
    //   if (currentUser) {
    //     if (currentEmail === newEmail) {
    //       throw new Error(NEW_EMAIL_EQUAL_CURRENT);
    //     }
    //
    //     if (currentUser.email !== currentEmail) {
    //       throw new Error(INCORRECT_EMAIL);
    //     }
    //
    //     const credential = EmailAuthProvider.credential(currentEmail, password);
    //     await reauthenticateWithCredential(currentUser, credential);
    //
    //     await HTTPService.requestEmailChange(currentEmail, newEmail);
    //   } else {
    //     throw new Error("missing:error_while_changing_email");
    //   }
    // } catch (error) {
    //   if (error instanceof FirebaseError) {
    //     throw new Error(error.code);
    //   } else if (error instanceof AxiosError) {
    //     throw new Error(error?.response?.data?.code || error.message);
    //   } else {
    //     const typedError = error as Error;
    //     throw new Error(typedError.message);
    //   }
    // }
  };

  const sendPasswordReset = async (email: string) => {
    return;

    // try {
    //   await sendPasswordResetEmail(auth, email);
    // } catch (error) {
    //   if (error instanceof FirebaseError) {
    //     throw new Error(error.code);
    //   } else {
    //     const typedError = error as Error;
    //     throw new Error(typedError.message);
    //   }
    // }
  };

  const resetPassword = async (oobCode: string, newPassword: string) => {
    return;

    // try {
    //   await verifyPasswordResetCode(auth, oobCode);
    //   await confirmPasswordReset(auth, oobCode, newPassword);
    // } catch (error) {
    //   if (error instanceof FirebaseError) {
    //     throw new Error(error.code);
    //   } else {
    //     const typedError = error as Error;
    //     throw new Error(typedError.message);
    //   }
    // }
  };

  const removeAccount = async () => {
    return;

    try {
      await HTTPService.deletePartnerAccount();
      await signOut(auth);
      setUser(null);
      setRecentlyLoggedOut(true);
    } catch (error) {
      const typedError = error as Error;
      throw new Error(typedError.message);
    }
  };

  const { isLoading } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      try {
        console.error("AUTH check");
        const id = localStorage.getItem("userId");
        if (!id) throw new Error("No user id");
        const response = await new UserApi().get(id);
        if (!response?._id || !response?.accountType || !response?.email) {
          throw new Error("Invalid user id");
        }
        assignUser(response as User);
      } catch (error) {
        logout();
        console.error("Error AUTH check", error);
        throw error;
      }
    },
    retry: false,
  });

  const { isLoading: profileLoading } = useQuery({
    queryKey: ["user-profile", user],
    queryFn: async () => {
      try {
        if (!user?._id || !user?.accountType) {
          return;
        }
        if (user.accountType === "partner") {
          addPartnerStoreSetup.setDefaultData(undefined, user);
        }

        const response = await new UserProfileApi().getByUserId(
          user._id,
          user.accountType
        );
        if (user.accountType === "partner") {
          addPartnerStoreSetup.setDefaultData(response as PartnerProfile, user);
          editPartnerStoreSetup.setDefaultData(response as PartnerProfile);
          setPartner(response as PartnerProfile);
        }
        if (user.accountType === "client") {
          setClient(response as ClientProfile);
        }
      } catch (error) {
        setClient(null);
        setPartner(null);
        console.error("user-profile", error);
        throw error;
      }
    },
    enabled: !!user,
    retry: false,
  });

  return (
    <AuthContext.Provider
      value={{
        user,
        recentlyLoggedOut,
        assignUser,
        partner,
        setPartner,
        client,
        setClient,
        isLoading: isLoading || profileLoading,
        signUp,
        signIn,
        logout,
        verifyEmail,
        changePassword,
        changeEmail,
        sendPasswordReset,
        resetPassword,
        removeAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
